import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { getControlFromFactory } from './ControlFactory';
import { validateFormField } from './FormFieldValidation';
import { ControlValueChangeArgs, DispatchEvent, DomainElement, FormControl, ObjectWithKeys, Page, State } from './SmartTypes';
import { axiosClient } from '../Service/axiosClient';
import pako from 'pako'; // Importing pako for zlib/gzip decompression
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';
import { Tooltip } from 'bootstrap';
import * as XLSX from 'xlsx';
import { pickDisplayJob } from '../Service/currentJobDisplay';

const CACHE_KEY = 'app-domain-data';
const TIMESTAMP_KEY = 'app-domain-data-timestamp';
const oneHourInMs = 1 * 60 * 60 * 1000;
const domainDataCacheExpiry = 55 * 60 * 1000; // 55 minutes

export const isEmpty = (value: any): any => {
    if (value === null || value === undefined) {
        return true;
    }

    if (value === '') return true;

    if (Array.isArray(value)) {
        // return value.length === 0;
        if (value.length === 0) {
            return true;
        }

        // checking whether the inner elements are empty to restrict empty nested array to be resulted as not empty;
        let isEmptyFlag = true;
        for (let i = 0; i < value.length; i++) {
            isEmptyFlag = isEmpty(value[i]);
            if (!isEmptyFlag) {
                return isEmptyFlag;
            }
        }

        return isEmptyFlag;
    }

    if (typeof value === 'object') {
        return Object.keys(value).length === 0 && value.constructor === Object;
    }

    if (value instanceof Map && value.size === 0) {
        return true;
    }

    return false;
};

export const getControlValueFromState = (
    key: string,
    state: State,
    defaultReturnValue: any = ''
): any[] | string | number | undefined | any => {
    if (isEmpty(state.data)) return [];
    if (key === '') return state.data as State;
    try {
        return key.split('.').reduce((a, c) => a[c], state.data);
    } catch (e) {
        return defaultReturnValue;
    }
    // return undefined;
};

// Hard coding here... Need to change this
export const getParentData = (key: string, state: State, defaultReturnValue: any = ''): any[] | string | number | undefined | any => {
    if (key === 'user.schoolId') {
        return getControlValueFromState('user.schoolId', state, null);
    }
    if (key.split('.').includes('schoolId')) return getControlValueFromState('schoolId', state, null);
    return getControlValueFromState(key, state);
};

export const handleControlValueChange = (args: ControlValueChangeArgs) => {
    const { dataKey, parentDataKey, dispatch, state, control, value } = args;

    const row = getControlValueFromState(parentDataKey, state as State);

    // Validate the control value for the validations error
    const errorMessages = validateFormField(control, value, state, control.props.label, dataKey, row);

    // Call the associated function
    if (state.actions[control.id])
        state.actions[control.id]({ control, value, row, dataKey, parentDataKey, state, dispatch, errorMessages });

    dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey, name: control.id, value, errorMessages } });
};

export const convertDomainArrayToMap = (domain: DomainElement[]) => {
    const domainMap = new Map<string, DomainElement[]>();

    domain.map((element) => {
        if (domainMap.has(element.categoryCode)) domainMap.get(element?.categoryCode)?.push(element);
        else domainMap.set(element.categoryCode, [element]);
    });

    return domainMap;
};

export const getDataStructureFromControls = (controls: FormControl[]) =>
    controls.reduce((accumulator, control) => Object.assign(accumulator, { [control.id]: undefined }), {});

// export const getDomainValueForCode = (code: string, domain: any[]) => {
//     if (isEmpty(code) || isEmpty(domain)) return '';
//     return domain?.find((element) => element.code === code)['value'];
// };

export const getDomainValueForCode = (code: string, domainCategoryCode: string, state: State) => {
    if (domainCategoryCode === 'AUTO_GEN_BASED_ON_CODE') return code;
    if (isEmpty(code) || isEmpty(domainCategoryCode)) return '';
    return (state?.domain?.get(domainCategoryCode)?.find((element) => element.code == code) as DomainElement)?.value ?? code;
};

export const getFlexColDomainValueForCode = (code: string, domainCategoryCode: string, state: State) => {
    if (domainCategoryCode === 'AUTO_GEN_BASED_ON_CODE') return code;
    if (isEmpty(code) || isEmpty(domainCategoryCode)) return '';
    return (state?.domain?.get(domainCategoryCode)?.find((element) => element.code == code) as DomainElement)?.flexCol ?? '';
};

export const isGrievanceCategoryPushedOnly = (categoryCode: string, state: State): boolean => {
    const flexColStr = getFlexColDomainValueForCode(categoryCode, 'GRIEVANCE_CATEGORY', state);
    if (isEmpty(flexColStr)) return false;
    try {
        const config = JSON.parse(flexColStr as string);
        return config.pushed_only === 1 || config.pushed_only === true;
    } catch {
        return false;
    }
};

export const getDomainValueForCodeUsingParent = (code: string, domainCategoryCode: string, state: State, parent: any) => {
    if (domainCategoryCode === 'AUTO_GEN_BASED_ON_CODE') return code;
    if (isEmpty(code) || isEmpty(domainCategoryCode || isEmpty(parent))) return '';

    return (
        (state?.domain?.get(domainCategoryCode)?.find((element) => element.code == code && element.parentCode == parent) as DomainElement)
            ?.value ?? code
    );
};

export const getDomainValueForCategoryCode = (domainCategoryCode: string, state: State) => {
    if (isEmpty(domainCategoryCode)) return [];

    const elements = (state?.domain?.get(domainCategoryCode) as DomainElement[]) ?? [];
    // console.log(elements);
    return elements.map((element) => ({
        code: element.code,
        value: element.value,
        flexCol: element.flexCol,
        parentCode: element.parentCode,
    }));
};

export const range = (from: number, to: number) => [...Array(to - from)].map((_, i) => i + from);

export const getFieldValuesConcatenatedFromRecord = (record: ObjectWithKeys, config: FormControl[], fieldNames: string, state: State) => {
    return fieldNames
        .split(',')
        .map((value) => value.trim())
        .map((fieldName) => {
            const controlConfig = config.find((ctrl) => ctrl.id === fieldName);
            return controlConfig?.props.domainCategoryCode
                ? getDomainValueForCode(record[fieldName] as string, controlConfig?.props.domainCategoryCode, state)
                : record[fieldName];
        })
        .join(' - ');
};

export const findTheRightDataKey = (control: FormControl, dataKey: string, parentDataKey: string) => {
    if (!control?.dataKey) return dataKey;
    return control?.dataKey === 'USE_PARENT' ? parentDataKey : control.dataKey;
};

// TODO: Merge this with evaluateExpressionWithValue
export const evaluateExpression = (expression: string, model: any, row: any = {}) => {
    if (isEmpty(model) || isEmpty(expression)) return;
    const dynamicFunc = (expression: string, model: any, row: any) => new Function('model', 'row', 'return ' + expression + ';');
    return dynamicFunc(expression, model, row)(model, row);
};

export const evaluateExpressionWithValue = (expression: string, data: any, model: any, row: any = {}) => {
    if (isEmpty(model) || isEmpty(expression)) return;
    const dynamicFunc = (expression: string) => new Function('model', 'row', 'data', 'return ' + expression + ';');
    console.log(dynamicFunc, ' dynamicFunc ');
    return dynamicFunc(expression)(model, row, data);
};

export const camelToSnakeCase = (str: string) => str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

export const camelize = (str: string) => {
    return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match, index) {
        if (+match === 0) return ''; // or if (/\s+/.test(match)) for white spaces
        return index === 0 ? match.toLowerCase() : match.toUpperCase();
    });
};

export const wordToHyphenCase = (str: string) => str?.replace(' ', '-')?.toLowerCase();

export const getIcon = (type: string, similarAlumni: boolean = true) => {
    type = wordToHyphenCase(type)?.toLowerCase();
    switch (type) {
        case 'award':
            type = similarAlumni ? type : 'medal';
            break;

        case 'exam-score':
            type = similarAlumni ? 'exam' : 'grade';
            break;

        case 'course-completion':
            type = similarAlumni ? 'course' : type;
            break;

        case 'social-service':
            type = similarAlumni ? 'social' : type;
            break;
    }

    return type;
};


/** True when designation is stored as a domain code (e.g. LEADERSHIP, EXECUTIVE_LEADERSHIP). */
export const isDomainDesignationCode = (value?: string | null): boolean => {
    if (!value?.trim()) return false;
    const normalized = value.trim();
    return /^[A-Z0-9_]+$/.test(normalized);
};

const DESIGNATION_DOMAIN_CATEGORIES = ['JOB_SENIORITY_LEVEL', 'DEFENCE_RANK'];

/**
 * Display designation on cards/profile/work experience.
 * - Free-text entries (e.g. "President & CEO") are shown as entered.
 * - All-caps domain codes (e.g. LEADERSHIP) are resolved from domain data.
 */
export const formatDesignationDisplay = (
    value?: string | null,
    state?: State,
    options?: { workingStatusCode?: string }
): string => {
    if (!value?.trim()) return '';
    const trimmed = value.trim();

    if (!isDomainDesignationCode(trimmed)) {
        return trimmed;
    }

    if (!state?.domain) {
        return trimmed.replace(/_/g, ' ');
    }

    const categories = [...DESIGNATION_DOMAIN_CATEGORIES];
    if (options?.workingStatusCode === 'IN_DEFENCE' || options?.workingStatusCode === 'DEFENCE') {
        categories.unshift('DEFENCE_RANK');
    }

    for (const category of categories) {
        const match = state.domain.get(category)?.find((element) => element.code == trimmed);
        if (match?.value) return match.value;
    }

    for (const [, elements] of state.domain) {
        const match = elements?.find((element) => element.code == trimmed);
        if (match?.value) return match.value;
    }

    return trimmed.replace(/_/g, ' ');
};

// export const isFormValid = (state: State, dispatch: (dispatchEvent: DispatchEvent) => void, errorKeysToIgnore: any = []) => {
//     if (
//         Object.entries(state?.formValidationErrors)
//             .filter(([key, value]) => !errorKeysToIgnore.includes(key) && value !== undefined)
//             .flat().length > 0
//     ) {
//         dispatch({ type: 'SHOW_ERRORS' });
//         console.log(state?.formValidationErrors);
//         // alert('Please enter the mandatory fields!');
//         return false;
//     }
//     return true;
// };

export const isFormValid = (state: State, dispatch: (dispatchEvent: DispatchEvent) => void, errorKeysToIgnore: any = []) => {
    const totalErrorCount = Object.entries(state?.formValidationErrors || {})
        .filter(([key, value]) => {
            const foundIgnoreKey = errorKeysToIgnore.find((ignoreKey: string) => {
                const regExp = new RegExp(`^${ignoreKey}(\\.\\d+)?$`); // Regular expression to match "key.<digit>"
                return regExp.test(key);
            });

            if (foundIgnoreKey) return false;
            // If not found and the value is defined, we should count the error (=> return true)
            return !isEmpty(value);
        })
        .reduce((total, [key, value]) => total + (value as any).length, 0);

    if (totalErrorCount > 0) {
        dispatch({ type: 'SHOW_ERRORS' });
        console.log(state?.formValidationErrors);
        return false;
    }
    return true;
};

// export const getDomainCategoryCodeForKey = (sectionName: string, key: string, state: State) => {
//     // TODO: controlGroup[0] in below is fishy
//     return state.formConfig?.sectionRepository
//         .find((section) => section.id === sectionName)
//         ?.controlGroup[0].controlGroup.find((control) => control.id === key)?.props.domainCategoryCode;
// };

export const formatDate = (date: Date, format: string) => {
    const map: any = {
        mo: date.toLocaleDateString(undefined, { month: 'short' }),
        mm: date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1,
        dd: date.getDate(),
        // yy: date.getFullYear().toString().slice(-2),
        yyyy: date.getFullYear(),
    };

    return format.replace(/mm|mo|dd|yyyy/gi, (matched) => map[matched]);
};

export const getControlPropsForCustomBuild = (
    sectionId: string,
    controlId: string,
    pageConfig: Page,
    handleChangeEvent: () => void = () => {}
) => {
    const formSection = pageConfig.sectionRepository.find((section) => section.id === sectionId);

    return {
        control: formSection?.controlGroup.find((control) => control.id === controlId) as FormControl,
        dataKey: `${formSection?.dataId ? formSection?.dataId : formSection?.id}.${controlId}`,
        parentDataKey: formSection?.dataId ? formSection?.dataId : formSection?.id,
        handleChangeEvent,
    };
};

export const getSectionConfig = (sectionName: string, state: State) =>
    state?.formConfig?.sectionRepository.find((section) => section.id === sectionName);

export const getDataKeyForSection = (section: string, state: State) => {
    if (getSectionConfig(section, state)?.dataId === 'NO_ID') return '';

    return getSectionConfig(section, state)?.dataId
        ? (getSectionConfig(section, state)?.dataId as string)
        : (getSectionConfig(section, state)?.id as string);
};

export const getChildKey = (control: FormControl, parentDataKey: string) => {
    if (!control.id) return parentDataKey;
    if (control.id && control?.dataId === 'USE_PARENT') return parentDataKey;
    if (control.id && control?.dataId?.length > 0 && control?.dataId !== 'USE_PARENT') return control.dataId;
    return isEmpty(parentDataKey) ? control.id : parentDataKey + '.' + control.id;
};

export const getControl = (control: FormControl, dataKey: string, state: State, padding: string) => {
    const childDataKey = control?.dataPath ? control.dataPath : getChildKey(control, dataKey);
    return getControlFromFactory(control, dataKey, childDataKey, state as State, null, padding);
};

export const getArrayIndexKey = (id: any) => (id ? id : uuidv4());

export const getUUIDs = (count: number) => {
    const uuids = [];
    for (let i = 0; i < count; i++) {
        uuids.push(uuidv4());
    }
    return uuids;
};

export function debounce(func: any, delay: number) {
    let timeout: any;
    const debounced = (...args: any) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            func(...args);
        }, delay);
    };
    debounced.cancel = () => {
        clearTimeout(timeout);
    };
    return debounced;
}

export function formatFullName(firstName: string, lastName: string) {
    const capitalize = (str: string) => {
        if (!str) return '';
        return str
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const formattedFirstName = capitalize(firstName);
    const formattedLastName = capitalize(lastName);

    return `${formattedFirstName} ${formattedLastName}`.trim();
}

/** Same top-options split as SelectControl (topOptionsGroupLabelInSelect / displayOrder). */
export function partitionTopDomainOptions(items: DomainElement[]) {
    const topItems = items
        .filter((item) => (item.displayOrder ?? 0) > 0)
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const topCodes = new Set(topItems.map((item) => item.code));
    const otherItems = items
        .filter((item) => !topCodes.has(item.code))
        .sort((a, b) => compareDomainFilterLabels(a.value, b.value));
    return { topItems, otherItems };
}

export const TOP_COMPANY_GROUP_LABEL = 'Top 10 Companies';
export const TOP_COLLEGE_GROUP_LABEL = 'Top 10 Colleges';

/** Filter keys whose API code list is top-N-by-count then alphabetical (companies / colleges). */
export const DIRECTORY_TOP_COUNT_FILTER_KEYS = new Set([
    'jobCompanyId',
    'universityId',
    'pgUniversityId',
]);

/** Case-insensitive label compare for directory filter checkbox lists. */
export function compareDomainFilterLabels(a: string, b: string): number {
    return (a || '').trim().localeCompare((b || '').trim(), undefined, {
        sensitivity: 'base',
        numeric: true,
    });
}

/**
 * Map API code list → domain rows in the same order as the API (count rank).
 * Clears domain-table displayOrder so it cannot hijack top-partition grouping.
 */
export function mapDomainElementsInCodeOrder(
    domainMap: Map<string, DomainElement[]> | undefined,
    categoryCode: string,
    codes: Array<string | number | null | undefined> | null | undefined
): DomainElement[] {
    if (!domainMap || isEmpty(codes) || !Array.isArray(codes)) return [];

    const byCode = new Map(
        (domainMap.get(categoryCode) || []).map((d) => [String(d.code), { ...d, displayOrder: undefined }])
    );

    return (codes || [])
        .map((code) => byCode.get(String(code)))
        .filter(Boolean) as unknown as DomainElement[];
}

/** Company filter: keep master-company rows, and add user-typed names that are not in COMPANY_CODE. */
export function appendCustomCompanyFilterOptions(
    elements: DomainElement[],
    codes: Array<string | number | null | undefined> | null | undefined
): DomainElement[] {
    const present = new Set((elements || []).map((d) => String(d.code)));
    const extras = dedupeFreeTextFilterOptions(
        (codes || [])
            .map((code) => (code == null ? '' : String(code).trim()))
            .filter((code) => code && !/^\d+$/.test(code) && !present.has(code))
    );
    return [
        ...(elements || []),
        ...extras.map((item) => ({
            categoryCode: 'COMPANY_CODE',
            code: item.code,
            value: item.value,
            parentCode: '',
            displayOrder: 0,
        })),
    ];
}

/**
 * Map domain rows to checkbox list: top `topN` by alumni count; equal counts sort A–Z by label.
 */
export function buildTopCountPartitionDomainList(
    elements: DomainElement[],
    countOrderedCodes: Array<string | number | null | undefined> | null | undefined,
    topN = 10,
    countsByCode?: Record<string, number> | null
): DomainElement[] {
    if (!elements?.length) return [];

    const getCount = (code: string | number) => {
        const key = String(code);
        if (countsByCode && countsByCode[key] != null) {
            return Number(countsByCode[key]) || 0;
        }
        return 0;
    };

    const cleared = elements.map((d) => ({ ...d, displayOrder: undefined as number | undefined }));

    const sorted = [...cleared].sort((a, b) => {
        const countDiff = getCount(b.code) - getCount(a.code);
        if (countDiff !== 0) return countDiff;
        return compareDomainFilterLabels(a.value, b.value);
    });

    const topItems = sorted.slice(0, topN).map((item, index) => ({
        ...item,
        displayOrder: index + 1,
    }));

    const restItems = sorted.slice(topN);
    return [...topItems, ...restItems] as DomainElement[];
}

/** Companion API field for ranked filters, e.g. jobCompanyId → jobCompanyIdCounts */
export function getDirectoryFilterCountsKey(filterKey: string): string {
    return `${filterKey}Counts`;
}

export function isCountryCodeDomain(domainCategory?: string | null): boolean {
    return domainCategory === 'COUNTRY_CODE';
}

export function toSentenceCase(str: string) {
    if (isEmpty(str)) return '';
    //need to check if param is not a number and not emapty
    const lowerCaseStr = str?.toLowerCase();
    const words = lowerCaseStr.split(' ');

    const sentenceCaseWords = words.map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
    });

    const sentenceCaseStr = sentenceCaseWords.join(' ');

    return sentenceCaseStr;
}

export const getDateDDMMYYYY = (date: Date) => moment(date).format('DD-MM-YYYY');
export const getDateHHMMA = (date: Date) => moment(date).format('hh:mm A');
// export const getDateDDMMYYYYHHMM = (date: Date) => moment(date).format('DD-MM-YYYY hh:mm A');
// export const getDateDDMMYYYYHHMM = (date: Date) => moment(date, 'YYYY-MM-DD HH:mm:ss').format('DD-MM-YYYY hh:mm A');
export const getDateDDMMYYYYHHMM = (dateString: Date) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
    //moment.utc(date).local().format('DD-MM-YYYY hh:mm A')};
};
export const parseDateStr = (dateStr: any, format: string = 'YYYY-MM-DD'): string => {
    if (dateStr == null) return '';
    // Trim the string and convert to upper case
    dateStr = dateStr.trim().toUpperCase();

    // Check if dateStr is only N
    if (dateStr === 'N') {
        return moment().format(format);
    }

    // Check if dateStr has valid format
    let regex = /^N([+-])(\d+)([DMY])$/;
    let matches = dateStr.match(regex);

    if (!matches) {
        throw new Error('Invalid format');
    }

    // Parse the operation, value and unit
    let operation = matches[1];
    let value = parseInt(matches[2]);
    let unit = matches[3].toLowerCase(); // moment.js expects lower case units

    // Perform operation
    let date;
    if (operation === '+') {
        date = moment().add(value, unit as moment.unitOfTime.DurationConstructor);
    } else if (operation === '-') {
        date = moment().subtract(value, unit as moment.unitOfTime.DurationConstructor);
    } else {
        throw new Error('Invalid operation');
    }

    // Format and return date
    return date.format(format);
};

export const setError = (dataKey: string, errorMessages: string[], dispatch: any) => {
    dispatch({ type: 'SET_FIELD_VALIDATION_ERRORS', payload: { dataKey, errorMessages } });
};

export function safelyAssign(target: any, path: (string | number)[], value: any): void {
    if (!Array.isArray(path)) {
        throw new Error('Path must be an array');
    }

    let obj: any = target;
    for (let i = 0; i < path.length - 1; i++) {
        if (obj[path[i]] === undefined) {
            return; // If any part of the path is undefined, abort assignment.
        }
        obj = obj[path[i]];
    }

    // Do the assignment if possible.
    if (obj[path[path.length - 1]] !== undefined) {
        obj[path[path.length - 1]] = value;
    }
}

export function isValidBusinessObject(data: any, mandatoryKeys: any, uniqueKey: any, dataKey: string, dispatch: any, name: string = '') {
    let ids = new Set();
    let isValid = true;
    let atLeastOneCompleteRow = false;

    data.forEach((item: any, index: number) => {
        // let isRowFilled = Object.values(item).some((value: any) => !isEmpty(value));
        let isRowFilled = mandatoryKeys.some(
            (key: any) =>
                (item.hasOwnProperty(key.id) && !isEmpty(item[key.id])) ||
                (key.manualColumnId && item.hasOwnProperty(key.manualColumnId) && !isEmpty(item[key.manualColumnId]))
        );

        let isRowComplete = mandatoryKeys.every(
            (key: any) =>
                (item.hasOwnProperty(key.id) && !isEmpty(item[key.id])) ||
                (key.manualColumnId && item.hasOwnProperty(key.manualColumnId) && !isEmpty(item[key.manualColumnId]))
        );

        mandatoryKeys.forEach((key: any) => {
            if (
                isRowFilled &&
                (!item.hasOwnProperty(key.id) || isEmpty(item[key.id])) &&
                (!key.manualColumnId || !item.hasOwnProperty(key.manualColumnId) || isEmpty(item[key.manualColumnId]))
            ) {
                setError(`${dataKey}.${index}`, [`Please enter "${key.label}" ${isEmpty(name) ? '' : ' for this record'}`], dispatch);
                isValid = false;
            } else {
                // No error for this key, clear previous error if exists
                setError(`${dataKey}.${index}`, [], dispatch);
            }
        });

        if (isRowComplete) {
            atLeastOneCompleteRow = true;
        }

        if (item.hasOwnProperty(uniqueKey.id)) {
            if (ids.has(item[uniqueKey.id])) {
                setError(`${dataKey}.${index}.${uniqueKey.id}`, [`Please remove duplicate entry for ${uniqueKey.label}`], dispatch);
                isValid = false;
            } else {
                if (!isEmpty(item[uniqueKey.id])) {
                    ids.add(item[uniqueKey.id]);
                }
                // No error for this key, clear previous error if exists
                setError(`${dataKey}`, [], dispatch);
            }
        }
    });

    if (!atLeastOneCompleteRow) {
        setError(`${dataKey}`, [`Please add at least one ${name ?? 'item'} with all mandatory fields`], dispatch);
        isValid = false;
    } else {
        // No error for this, clear previous error if exists
        setError(`${dataKey}`, [], dispatch);
    }

    return isValid;
}

export const convertToPercentage = (data: any) => {
    // First, determine the total count
    const totalCount = data.reduce((sum: number, item: any) => sum + item.value, 0);

    // Map over the array and compute the percentage for each entry
    return data.map((item: any) => ({
        label: item.label,
        value: parseFloat(((item.value * 100) / totalCount).toFixed(1)), // Rounded to one decimal
        actualValue: item.value,
    }));
};

// Note: Magic happens here.. handle with care
export const getFilteredDomain = (domainCode: string, state: State, parentCode: any = []) => {
    const getRealDomainCode = (domainCode: string) => {
        switch (domainCode) {
            case 'PREFERENCE_COUNTRY_CODE':
                return 'COUNTRY_CODE';
            case 'PREFERENCE_FIELD_OF_STUDY_CODE':
                return 'FIELD_OF_STUDY_TYPE_CODE';
            default:
                return domainCode;
        }
    };

    const domain = state.domain.get(getRealDomainCode(domainCode));
    if (!domain) return [];

    const domainToFilterMap: any = {
        PAST_YEAR_CODE: 'passOutYear',
        CLASS_CODE: 'classCode',
        CLASS_12_CURRICULUM_CODE: 'curriculumCode',
        PROGRAM_LEVEL: 'pursuingEducationLevelCode',
        COUNTRY_CODE: 'residingCountryCode',
        CITY_CODE: 'residingCityCode',
        PREFERENCE_COUNTRY_CODE: 'preferenceCountryCode',
        PREFERENCE_FIELD_OF_STUDY_CODE: 'preferenceFieldOfStudyCode',
        FIELD_OF_STUDY_TYPE_CODE: 'fieldOfStudyCode',
        SCHOOL_CODE: 'collegeCampusCode',
        INSTITUTE_PROGRAM_CODE: 'programId',
        INSTITUTE_DOMAIN: 'instituteId',
    };

    const filterDomainCodes = state.data.filterDomains[domainToFilterMap[domainCode]];
    let sortedDomainCodes = filterDomainCodes;

    if (domainCode === 'PAST_YEAR_CODE' && filterDomainCodes) {
        sortedDomainCodes = [...filterDomainCodes].sort((a: any, b: any) => b - a); // Sort in descending order
        let values: any = [];
        sortedDomainCodes.forEach((year: any) => {
            values.push({
                categoryCode: 'PAST_YEAR_CODE',
                code: String(year),
                displayOrder: 127,
                flexCol: null,
                parentCode: null,
                value: String(year),
            });
        });
        return values;
    }

    return sortedDomainCodes
        ?.map((code: string) => {
            return domain.find((item: any) => {
                if (item.code != code) return false;

                // Check if parentCode is an array
                if (Array.isArray(parentCode)) {
                    if (!isEmpty(parentCode) && !parentCode.map(String).includes(String(item.parentCode))) return false;
                } else {
                    if (!isEmpty(parentCode) && String(item.parentCode) != String(parentCode)) return false;
                }

                return true;
            });
        })
        .sort((a: any, b: any) => a.displayOrder - b.displayOrder)
        .filter((item: any) => item);
};

export const getFuzzySearchCriteria = (fuzzySearchText: any) => {
    if (isEmpty(fuzzySearchText)) return {};

    const isUUID = (input: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(input);
    const isName = (input: string) => /^[a-zA-Z\s]+$/.test(input);
    const isEmail = (input: string) => /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(input);
    const isMobile = (input: string) => /^\+?\d{1,4}[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/.test(input);
    const isGrievanceCode = (input: string) => /^GRV-\d{4}-\d{4}$/.test(input);

    if (isEmail(fuzzySearchText)) return { registeredEmailId: fuzzySearchText };
    if (isMobile(fuzzySearchText)) return { mobileNumber: fuzzySearchText };
    if (isUUID(fuzzySearchText)) return { userId: fuzzySearchText };
    if (isGrievanceCode(fuzzySearchText)) return { grievanceCode: fuzzySearchText };
    if (isName(fuzzySearchText)) return { name: fuzzySearchText };

    return { name: fuzzySearchText };
};

export const canEditProfile = (profileId: string, sessionState: any) => {
    const loggedInUserId = sessionState?.id;
    const loggedInUserTypeCode = sessionState?.userTypeCode;

    if (loggedInUserId === profileId) {
        return true;
    }

    if ((loggedInUserTypeCode === 'STUDENT' || loggedInUserTypeCode === 'ALUMNI') && loggedInUserId !== profileId) {
        return false;
    }
};

export const arrayColumn = (a: any, i: any, ok?: any) => {
    return (
        a
            .reduce((c: any, v: any, k: any) => (ok === undefined ? [(c[k] = v[i]), c][1] : [(c[v[ok]] = i === null ? v : v[i]), c][1]), [])
            // .map((s: any) => s?.trim()); // @by RG @note trim function throws an error if the type of variable s is not string;
            .map((value: any) => (typeof value === 'string' ? value.trim() : value))
    );
};

/**
 * @func arrayUnique returning unique array;
 * @param {Array} arr
 * @param {boolean} omitCase
 * @returns unique array;
 * @dt 8th.Aug,2K23 @by RG;
 */
export const arrayUnique = (arr: any, omitCase: boolean) => {
    if (!omitCase) {
        return [...arr.values()];
    }

    arr = new Map(
        arr.map((s: any) => {
            return [s?.toLowerCase(), s];
        })
    );

    return [...arr.values()];
};

/**
 * @func getTimeAgo returning time ago;
 * @param {string} timestamp
 * @returns time ago
 * @dt 12th.Aug,2K23 @by RG;
 */
export const getTimeAgo = (timestamp: any) => {
    return moment(timestamp).fromNow();
};

/**
 * @func triggering click event on next button on hitting enter key;
 * @param event key down event;
 * @dt 19th.Sep,2K23 @by RG;
 */
export const handleNextBtnClickOnEnter = (event: any) => {
    if (event.key === 'Enter') {
        // Prevent the default Enter key behavior (e.g., form submission)
        event.preventDefault();

        const nextButton = document.querySelector('.nextbtn');

        // Check if the element exists
        if (nextButton) {
            // Create and dispatch a click event on the element
            const clickEvent = new Event('click', { bubbles: true });
            nextButton.dispatchEvent(clickEvent);
        } else {
            // Handle the case where the 'nextbtn' element is not found
            console.error("Element with class 'nextbtn' not found.");
        }
    }
};

export const initNextBtnClickOnEnterEvent = () => {
    // Add the event listener
    window.addEventListener('keydown', handleNextBtnClickOnEnter);

    // Clean up the event listener when the component unmounts
    return () => {
        window.removeEventListener('keydown', handleNextBtnClickOnEnter);
    };
};

export function numberToOrdinal(n: number) {
    const s = ['th', 'st', 'nd', 'rd'],
        v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * creating dynamic file name for file to be downloaded;
 * @param pageName page name;
 * @param data data to be exported;
 */
export function getDownloadFileName(pageName: string, data: any) {
    let fName = 'download';

    switch (pageName) {
        case 'internal-admin-benefits-report':
            fName = 'Benefits report';
            break;
        case 'conversation-report':
            fName = 'Conversation Report';
            break;
    }

    return `${fName} (${data.length})`;
}

export async function hashEmail(email: string) {
    const msgBuffer = new TextEncoder().encode(email); // Encode as UTF-8
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer); // Hash the message
    const hashArray = Array.from(new Uint8Array(hashBuffer)); // Convert to byte array
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''); // Convert bytes to hex string
    return hashHex;
}

export function isSchoolAdmin() {
    const user = JSON.parse(localStorage.getItem('user') as string);
    const schoolAdminRoles = ['INSTITUTE_ADMIN', 'INSTITUTE_SUPPORT_MANAGER', 'INSTITUTE_MARKETING_MANAGER', 'SCHOOL_ADMIN'];
    return schoolAdminRoles.includes(user?.role ? user?.role : user?.userTypeCode);
}

export const canExportToExcel = (sessionState: any) =>
    ['SCHOOL_ADMIN', 'INSTITUTE_ADMIN'].includes(sessionState?.userTypeCode ?? '');

const getExportRows = (data: any[], pageName: string, state?: State): any[] => {
    const yesNo = (value: any) => (value == 1 ? 'Yes' : 'No');
    const formatDate = (value: any) => (isEmpty(value) ? '' : moment(value).format('DD-MM-YYYY'));
    const domainValue = (code: any, categoryCode: string) =>
        state ? getDomainValueForCode(code, categoryCode, state) : code;

    switch (pageName) {
        case 'alumni-directory':
            return data.map((s: any) => ({
                ID: s.uuid,
                Name: s.name,
                Gender: s.genderCode,
                'User Type': s.userTypeCode,
                'Pass Out Year': s.passOutYear,
                'Residing Country': domainValue(s.residingCountryCode, 'COUNTRY_CODE'),
                'Residing City': domainValue(s.residingCityCode, 'CITY_CODE'),
                'Program Name': s.programName,
                'Institute Name': s.instituteName,
                'Present Education Status': s.presentEducationStatusCode,
                'Working Status': domainValue(s.workingStatusCode, 'WORK_STATUS_CODE'),
                'Company Name': s.companyName,
                Designation: s.designation,
                Industry: domainValue(s.industryCode, 'INDUSTRY_CODE'),
                'Functional Area': domainValue(s.functionalAreaCode, 'FUNCTIONAL_AREA_CODE'),
                'Seniority Level': domainValue(s.seniorityLevelCode, 'JOB_SENIORITY_LEVEL'),
                'Type of Practice': domainValue(s.typeOfPracticeCode, 'PROFESSIONAL_PRACTICE_TYPE'),
                Notable: yesNo(s.isNotable),
            }));

        case 'domain-expert-directory':
            return data.map((s: any) => ({
                ID: s.uuid,
                Name: s.name,
                // Email: s.email,
                Program: s.programName,
                Batch: s.passOutYear,
                'Domain Expertise': s.domainExpertiseArea,
                Company: s.companyName,
                Designation: s.designation,
                'AT Stage': s.atStage,
                'CG Stage': s.cgStage,
            }));

        case 'entrepreneur-directory':
            return data.map((s: any) => {
                // Same entrepreneurship job the directory card shows, not the profile's scalar job.
                const job: any = pickDisplayJob(s.currentJobsJson, 'ENTREPRENEURSHIP').job || {};
                return {
                    ID: s.uuid,
                    Name: s.name,
                    Program: s.programName,
                    Batch: s.passOutYear,
                    Campus: s.instituteName,
                    'Founder Status': domainValue(job.founderStatus, 'JOB_FOUNDER_STATUS'),
                    Company: job.companyName,
                    Designation: job.designation,
                    Industry: domainValue(job.industryCode, 'INDUSTRY_CODE'),
                    'Establishment Year': job.establishmentYear,
                    'Operational Status': domainValue(job.operationalStatus, 'COMPANY_OPERATIONAL_STATUS'),
                    Revenue: domainValue(job.annualRevenue, 'COMPANY_ANNUAL_REVENUE'),
                    'Employee Count': domainValue(job.companySize, 'COMPANY_EMPLOYEE_COUNT'),
                    'Residing Country': domainValue(s.residingCountryCode, 'COUNTRY_CODE'),
                    'Residing City': domainValue(s.residingCityCode, 'CITY_CODE'),
                };
            });

        case 'business-directory':
            return data.map((p: any) => ({
                ID: p.providerId,
                'Alumni Name': p.alumniName,
                'Business Name': p.businessName,
                Category: domainValue(p.category, 'BUSINESS_CATEGORY'),
                Country: domainValue(p.country, 'COUNTRY_CODE'),
                City: domainValue(p.city, 'CITY_CODE'),
                Experience: p.experience,
                Availability: p.availability,
                'Website URL': p.websiteUrl,
                Status: p.status,
            }));

        case 'track-student-profiles':
            return data.map((s: any) => ({
                ID: s.uuid,
                Name: formatFullName(s.firstName, s.lastName),
                'Program Name': s.programName,
                'Pass Out Year': s.passOutYear,
                Campus: s.schoolName,
                'Created On': formatDate(s.createdAt),
                'Modified On': formatDate(s.updatedAt),
            }));

        case 'track-student-career-preferences':
            return data.map((s: any) => ({
                ID: s.uuid,
                Name: formatFullName(s.firstName, s.lastName),
                Campus: s.schoolName,
                'Career Preferences': s.fieldOfStudyPreferenceList,
                'Country Preferences': s.countryPreferenceList,
                'College Preferences': s.universityPreferenceList,
            }));

        case 'track-alumni-profiles':
            return data.map((s: any) => ({
                ID: s.uuid,
                Name: formatFullName(s.firstName, s.lastName),
                Campus: s.schoolName,
                'Present Education Status': domainValue(s.presentEducationStatusCode, 'PRESENT_EDUCATION_STATUS_CODE'),
                'Working Status': domainValue(s.workingStatus, 'WORK_STATUS_CODE'),
                'Profile Completion %': s.profileCompletionPercentage,
                City: domainValue(s.residingCityCode, 'CITY_CODE'),
                Country: domainValue(s.residingCountryCode, 'COUNTRY_CODE'),
                'Created On': formatDate(s.createdAt),
                'Membership Fees Payment Status': domainValue(s.membershipFeePaymentStatusCode, 'MEMBERSHIP_FEE_PAYMENT_STATUS_CODE'),
            }));

        case 'track-alumni-education-preferences':
            return data.map((s: any) => ({
                ID: s.uuid,
                Name: formatFullName(s.firstName, s.lastName),
                Campus: s.schoolName,
                'Present Education Status': domainValue(s.presentEducationStatusCode, 'PRESENT_EDUCATION_STATUS_CODE'),
                'UG Course': domainValue(s.fieldOfStudyCode, 'FIELD_OF_STUDY_TYPE_CODE'),
                'PG Course': domainValue(s.pgFieldOfStudyCode, 'FIELD_OF_STUDY_TYPE_CODE'),
                'UG University': s.universityName,
                'PG University': s.pgUniversityName,
                'Prestigious College (UG)': yesNo(s.isPrestigiousCollege),
                'Prestigious College (PG)': yesNo(s.pgIsPrestigiousCollege),
            }));

        case 'alumni-birthdays':
            return data.map((a: any) => ({
                ID: a.studentUuid,
                Name: formatFullName(a.firstName, a.lastName),
                'Current Role': a.currentRole,
                'Current Company': a.currentCompany,
                Program: a.programName,
                'Pass Out Year': a.passOutYear,
                City: a.city,
                'Date of Birth': formatDate(a.dateOfBirth),
            }));

        default:
            return data;
    }
};

const EXPORT_FILE_PREFIX: Record<string, string> = {
    'alumni-directory': 'Alumni_Directory',
    'domain-expert-directory': 'Domain_Experts',
    'entrepreneur-directory': 'Entrepreneur_Directory',
    'business-directory': 'Business_Directory',
    'track-student-profiles': 'Student_Profiles',
    'track-student-career-preferences': 'Student_Career_Preferences',
    'track-alumni-profiles': 'Alumni_Profiles',
    'track-alumni-education-preferences': 'Alumni_Education_Details',
    'alumni-birthdays': 'Upcoming_Birthdays',
};

export const exportToExcel = (data: any[], pageName: string, state?: State) => {
    try {
        const rows = getExportRows(data ?? [], pageName, state);
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');

        const fileNamePrefix = EXPORT_FILE_PREFIX[pageName] ?? 'Export';
        const downloadFileName = `${fileNamePrefix}_${moment().format('DD-MM-YYYY_HH-mm-ss')}`;
        XLSX.writeFile(workbook, `${downloadFileName}.xlsx`);
    } catch (error) {
        console.error('Error exporting to Excel:', error);
    }
};

export function isSuperAdmin() {
    const user = JSON.parse(localStorage.getItem('user') as string);
    const superAdminRoles = ['SUPER_ADMIN'];
    return superAdminRoles.includes(user?.role ? user?.role : user?.userTypeCode);
}

export function isVibgyorInstitute() {
    const vibgyorInstituteIds = [
        "10000005493", // adding miles for testing
        "10010024805","10000008052","10010024798","10010024799","10010024800","10010024801","10010024802","10010024803","10010024804","10010024806","10010024807","10010024808","10010024809","10010024810","10010024811","10010024812","10010024813","10010024814","10010024815","10010024816","10010024825","10010024817","10010024818","10010024819","10010024820","10010024821","10010024822","10010024823","10010024824","10010024826","10010024827","10010024828","10010024829","10010024830","10010024831","10010024832","10010024833","10010024834","10010024835"
      ];
    const schoolSettings = JSON.parse(localStorage.getItem('school_settings') as string);
    return vibgyorInstituteIds.includes(''+schoolSettings?.id);
}

export async function activateUserSession(sessionState: any, sessionDispatch: any) {
    await axiosClient().post(`${process.env.REACT_APP_COMMON_API}/profile/update-status`, { profileStatusCode: 'step-two' });

    let sessionStateData = { ...sessionState, userStatusCode: 'ACTIVE', profileStatusCode: 'COMPLETE' };
    // let instData = [...sessionStateData.institutes];
    // instData[0] = { ...instData[0], userStatusCode: 'ACTIVE', profileStatusCode: 'COMPLETE' };
    // sessionStateData.institutes = instData;

    //axiosClient().post(`${process.env.REACT_APP_COMMON_API}/profile/update-status`, { profileStatusCode: 'step-two' });
    sessionDispatch({ type: 'SET', payload: sessionStateData });
    return true;
}

export function recentPassoutYear(alumni: any) {
    let recentYear = 0;

    // Check if postGradCollege array exists and has elements
    if (alumni?.postGradCollege?.length > 0) {
        recentYear = alumni?.postGradCollege[0]?.endYear; //for max pasosutyear
    }

    // Check if underGradCollegeJoined exists
    if (recentYear === 0 && alumni?.underGradCollegeJoined?.endYear) {
        recentYear = alumni.underGradCollegeJoined.endYear;
    }
    // Check if selectedInstitute exists
    if (recentYear === 0 && alumni?.selectedInstitute?.endYear) {
        recentYear = alumni.selectedInstitute.endYear;
    }

    return recentYear;
}

export function handleCopyText(text: any, id: any): void {
    navigator.clipboard.writeText(text);
    const element = document.getElementById(id);

    let icon = `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-check" viewBox="0 0 16 16">
        <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
      </svg>
    `;
    if (element) {
        element.innerHTML = icon;
    }
    setTimeout(() => {
        icon = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-copy" viewBox="0 0 16 16">
            <path fill-rule="evenodd" d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"/>
        </svg>
        `;
        if (element) {
            element.innerHTML = icon;
        }
    }, 1000);
}

export function getMenuType(data: any) {
    const alumniTypeProgramLevel = ['UG', 'PG', 'Doctoral'];
    let menuType = 'ALUMNI';

    const isAlumni =
        (!isEmpty(data?.pursuingEducationLevelCode) && alumniTypeProgramLevel.includes(data?.pursuingEducationLevelCode)) ||
        (!isEmpty(data?.highestCompletedEducationLevelCode) && alumniTypeProgramLevel.includes(data?.highestCompletedEducationLevelCode));
    if (isAlumni) {
        return menuType;
    }

    if (
        (!alumniTypeProgramLevel.includes(data?.programLevel) && data?.programLevel < 12) ||
        (data?.programLevel == 12 && data?.userTypeCode == 'STUDENT')
    ) {
        menuType = 'STUDENT';
    }

    return menuType;
}

/** Study-preferences route slug based on education level (matches ProgramBasicInfoActions HIGHER_STUDIES routing). */
export function getStudyPreferencePath(userId: string, sessionState: any): string {
    const { presentEducationStatusCode, pursuingEducationLevelCode, highestCompletedEducationLevelCode, programLevel } =
        sessionState ?? {};

    let level =
        presentEducationStatusCode === 'PURSUING_STUDIES' ? pursuingEducationLevelCode : highestCompletedEducationLevelCode;
    if (isEmpty(level)) {
        level = programLevel;
    }

    switch (level) {
        case '12':
            return `/alumni/${userId}/ug-education-preference`;
        case 'UG':
        case 'PG':
        case 'Doctoral':
            return `/alumni/${userId}/pg-education-preference`;
        default: {
            const numericLevel = parseInt(level, 10);
            if (!isNaN(numericLevel) && numericLevel >= 1 && numericLevel < 12) {
                return `/alumni/${userId}/student-preference`;
            }
            return `/alumni/${userId}/pg-education-preference`;
        }
    }
}

export function extractVideoId(url: string) {
    if (url === undefined) return '';
    const videoIdMatch = url?.match(/(?:youtu.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
    return videoIdMatch ? videoIdMatch[1] : '';
}

export function getWhiteLabelUrl(sessionState: any) {
    if (sessionState && !isEmpty(sessionState.whitelabelUrl)) {
        return sessionState.whitelabelUrl;
    } else {
        const baseUrl = process.env.REACT_APP_APP_BASE_URL;
        return `${baseUrl}/`;
    }
}

/**
 * @func converting a normal array into associative array;
 * @dt 25th.June,2k24 @by RG;
 */
export function getAssociativeArray(data: any, idx: any) {
    return data.reduce((acc: any, item: any) => {
        acc[item[idx]] = item;
        return acc;
    }, {});
}

/**
 * @func converting a normal array into multi associative array;
 * @dt 18.July,2k24 @by RG;
 */
export function getMultiAssociativeArray(data: any, idx: any, childKey = '') {
    if (isEmpty(data)) {
        return {};
    }

    return data.reduce((acc: any, item: any) => {
        const key = item[idx];
        if (!acc[key]) {
            acc[key] = [];
        }
        if (isEmpty(childKey)) {
            acc[key].push(item);
        } else {
            acc[key][childKey] = item;
        }
        return acc;
    }, {});
}

export const triggerGridDataOnChange = (data: any, parentKey: any, dataKey: any) => {
    if (isEmpty(data)) {
        return;
    }
    // Assuming you have an onChange handler defined somewhere
    data.forEach((item: any, index: any) => {
        const event = new Event('change', { bubbles: true });
        const inputElement = document.querySelector(`[name="${parentKey}.${index}.${dataKey}"]`);
        if (inputElement) {
            inputElement.dispatchEvent(event);
        }
    });
};

export const getValueFromPath = (obj: any, path: any) => {
    return path.split('.').reduce((acc: any, part: any) => acc && acc?.[part], obj);
};

export const getProgramLevelByProgramId = async (programId: string) => {
    try {
        const programResponse = await axiosClient().post(`${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/admin/get-program-level-by-program-id`, { programId : programId });
        if (programResponse.status === 200) {
            return programResponse.data?.data?.programLevel || '';
        }
        return '';
    } catch (error) {
        console.error('Error fetching program level:', error);
        return '';
    }
};


/**
 * Checks if a given file URL corresponds to a valid image file based on its extension.
 * Valid image file extensions are `.jpg`, `.jpeg`, `.png`, `.gif`, `.bmp`, and `.webp`.
 *
 * @param {string} fileUrl - The URL or path of the file to be checked. It is expected to be a string that includes the file extension.
 * @returns {boolean} `true` if the file has a valid image extension, otherwise `false`.
 *
 * @example
 * const url1 = "http://example.com/image.jpg";
 * const url2 = "http://example.com/document.pdf";
 *
 * console.log(isValidImageFile(url1)); // Output: true
 * console.log(isValidImageFile(url2)); // Output: false
 *
 * @dt 5th.Sep,2K24 @by RG;
 */
export const isValidImageFile = (fileUrl: any): boolean => {
    const fileExtension = fileUrl.substring(fileUrl.lastIndexOf('.') + 1).toLowerCase();

    // List of valid image file extensions
    const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];

    // Check if the file extension is valid
    return validExtensions.includes(fileExtension);
};

/**
 * Checks if a given URL is a valid video URL based on specified platforms.
 *
 * @param {string} url - The URL to validate.
 * @param {Array<string>} [platforms=['youtube', 'vimeo']] - An array of platform names to validate against. Defaults to ['youtube', 'vimeo'].
 * @returns {boolean} - Returns `true` if the URL matches the regex pattern for any of the specified platforms, otherwise returns `false`.
 *
 * @throws {Error} - Throws an error if an unsupported platform is passed.
 *
 * @example
 * // Validate a YouTube URL
 * isValidVideoUrl('https://www.youtube.com/watch?v=dQw4w9WgXcQ'); // true
 *
 * @example
 * // Validate a Vimeo URL
 * isValidVideoUrl('https://vimeo.com/12345678', ['vimeo']); // true
 *
 * @example
 * // Validate a URL with unsupported platform
 * try {
 *     isValidVideoUrl('https://example.com/video', ['unsupportedPlatform']);
 * } catch (e) {
 *     console.error(e.message); // "Platform(s) unsupportedPlatform are not configured"
 * }
 */
export const isValidVideoUrl = (url: string, platforms: Array<string> = ['youtube', 'vimeo']): boolean => {
    // Define regex patterns for supported platforms
    const platformPatterns: { [key: string]: RegExp } = {
        youtube:
            /^(https?\:\/\/)?(www\.)?(youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.+?\?v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\?.*)?$/,
        vimeo: /^(https?:\/\/)?(www\.)?(vimeo\.com\/)((channels\/[\w]+\/)|(groups\/[\w]+\/)|(album\/\d+\/)?(video\/)?|)(\d+)(?:[?&].*)?$/,
    };

    // Check for unsupported platforms
    const unsupportedPlatforms = platforms.filter((platform) => !platformPatterns[platform]);
    if (unsupportedPlatforms.length > 0) {
        throw new Error(`Platform(s) ${unsupportedPlatforms.join(', ')} are not configured`);
    }

    // Validate URL based on the specified platforms
    return platforms.some((platform) => {
        const regex = platformPatterns[platform];
        return regex ? regex.test(url) : false;
    });
};

// export const arrayColumn = (data: [], columnKey: string, indexKey: string) => {
//     if (!Array.isArray(data)) {
//         throw new TypeError('data must be an array');
//     }

//     return data.reduce((result, item) => {
//         const value = item[columnKey];
//         if (indexKey) {
//             const index = item[indexKey];
//             result[index] = value;
//         } else {
//             result.push(value);
//         }
//         return result;
//     }, indexKey ? {} : []);
// }

export const updateMoEngageEvent = async (data: any, url: string) => {
    try {
        const response = await axiosClient().post(`${process.env.REACT_APP_MOENGAGE_API}/moengage/${url}`, {
            userInfo: data,
        });
        if (response.status === 200) {
            return true;
        }
    } catch (error) {
        console.error('Error posting user information to MoEngage:', error);
    }
    return false;
};

export const olddecompressData = (compressedData: any) => {
    try {
        // Convert base64 to a Uint8Array
        const binaryString = atob(compressedData);
        const binaryData = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            binaryData[i] = binaryString.charCodeAt(i);
        }

        // Decompress the binary data using pako
        const decompressedBuffer = pako.inflate(binaryData);
        const decompressedData = new TextDecoder('utf-8').decode(decompressedBuffer);

        const parsedData = JSON.parse(decompressedData);

        // Check if the data follows the new structure
        if (Array.isArray(parsedData) && parsedData[0]?.data_value) {
            return parsedData[0].data_value; // Return the nested 'data_value' array
        }

        return parsedData; // Fallback if the structure is different
    } catch (error) {
        console.error('Error decompressing data:', error);
        return null;
    }
};

export const getOldDomainData = async () => {
    const CACHE_KEY = 'app-domain-data';
    const TIMESTAMP_KEY = `app-domain-data-timestamp`;

    // Check if the data is in localStorage and if it's still valid
    const cachedData = localStorage.getItem(CACHE_KEY);
    const cachedTimestamp = localStorage.getItem(TIMESTAMP_KEY);
    const oneHourInMs = 1 * 60 * 60 * 1000;

    if (cachedData && cachedTimestamp && Date.now() - parseInt(cachedTimestamp) < oneHourInMs) {
        return olddecompressData(cachedData); // Use cached data if it's still valid
    }

    // Fetch new data if the cache is expired or missing
    const response = await axiosClient().get(`${process.env.REACT_APP_PAGE_CONFIG_SERVER}/v1/appConfig/domain_data`);

    // Save new data and its timestamp in localStorage
    localStorage.setItem(CACHE_KEY, response.data);
    localStorage.setItem(TIMESTAMP_KEY, Date.now().toString());

    return olddecompressData(response.data);
};

export const syncProfileMarker = async (uuid: String) => {
    await axiosClient().get(`${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/markers/sync-marker-messages-by-uuid/${uuid}`);
};

/**
 * Initializes Bootstrap tooltips on all elements with the `data-bs-toggle="tooltip"` attribute.
 *
 * This function selects all elements in the DOM with the `data-bs-toggle="tooltip"` attribute 
 * and instantiates a new Tooltip instance for each of them. It leverages the Bootstrap Tooltip 
 * class to enable and configure the tooltip behavior.
 *
 * @param Tooltip - The Tooltip class from Bootstrap. It must be imported and passed into this function.
 *
 * @usage
 * // Add `data-bs-toggle="tooltip"` attribute to all elements where tooltips are needed.
 * import { Tooltip } from 'bootstrap';
 * 
 * useEffect(() => {
 *      initTooltip(Tooltip); // Call this function on page load or component mount.
 * }, []);
 */
export const initTooltip = (Tooltip: any) => {
    document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => Tooltip && new Tooltip(el));
};

/**
 * Fetches the Content-Type of the resource located at the specified URL.
 *
 * @param {string} url - The URL of the resource.
 * @returns {Promise<string|null>} A promise that resolves to the Content-Type if available; otherwise, null.
 * @throws {TypeError} If the input URL is not a string.
 * @throws {Error} If the request fails for reasons other than a network error.
 */
export const getContentType = async (url: string): Promise<string | null> => {
    if (typeof url !== 'string') {
        throw new TypeError('The input URL must be a string.');
    }

    try {
        // const response = await axios.head(url);
        const response = await axiosClient().get(`${process.env.REACT_APP_COMMON_API}/social/get-headers`, {
            params: { url: url },
        });

        console.log(response, ' response ');
        return response.data.data['content-type'] || null;
    } catch (error: any) {
        if (error.response) {
            // The request was made, and the server responded with a status code outside the 2xx range
            console.error(`Request failed with status code ${error.response.status}`);
        } else if (error.request) {
            // The request was made, but no response was received
            console.error('No response received:', error.request);
        } else {
            // Something happened in setting up the request
            console.error('Error setting up the request:', error.message);
        }
        return null;
    }
};

export const getHeaders = async (url: string) => {
    if (typeof url !== 'string') {
        throw new TypeError('The input URL must be a string.');
    }

    try {
        // const response = await axios.head(url);
        const response = await axiosClient().get(`${process.env.REACT_APP_COMMON_API}/social/get-headers`, {
            params: { url: url },
        });

        console.log(response, ' response ');
        return response?.data?.data || {};
    } catch (error: any) {
        if (error.response) {
            // The request was made, and the server responded with a status code outside the 2xx range
            console.error(`Request failed with status code ${error.response.status}`);
        } else if (error.request) {
            // The request was made, but no response was received
            console.error('No response received:', error.request);
        } else {
            // Something happened in setting up the request
            console.error('Error setting up the request:', error.message);
        }
        return {};
    }
};

export const getVideoCodec = async (file: any): Promise<string> => {
    return new Promise((resolve, reject) => {
        if (!file) {
            reject('No file provided');
            return;
        }

        // Read only the first 1MB of the file (most codec info is in the header)
        const chunkSize = 1024 * 1024; // 1MB
        const blob = file.slice(0, chunkSize);
        const reader = new FileReader();

        reader.onload = (e: any) => {
            const buffer = e.target.result;
            const uint8Array = new Uint8Array(buffer);
            let headerText = '';
            // Convert the first bytes to a string
            for (let i = 0; i < uint8Array.length; i++) {
                headerText += String.fromCharCode(uint8Array[i]);
            }

            console.log(headerText, ' headerText ');
            // Search for codec signatures.
            // H.264 is usually indicated by "avc1"
            // H.265 is usually indicated by "hvc1" or "hev1"
            if (headerText.indexOf('avc1') !== -1) {
                resolve('H.264');
            } else if (headerText.indexOf('hvc1') !== -1 || headerText.indexOf('hev1') !== -1) {
                resolve('H.265');
            } else {
                resolve('unknown');
            }
        };

        reader.onerror = () => {
            reject('Error reading file');
        };

        reader.readAsArrayBuffer(blob);
    });
};

export const getVideoFrameRate = async (file: any): Promise<number> => {
    return new Promise((resolve, reject) => {
        if (!file) {
            reject('No file provided');
            return;
        }

        // Create a video element
        const video = document.createElement('video');
        video.src = URL.createObjectURL(file);

        // Set up event listeners
        video.onloadedmetadata = () => {
            // Calculate frame rate
            const duration = video.duration; // Total duration in seconds
            let frameCount = 0;

            // Seek through the video to count frames
            const seek = () => {
                if (video.currentTime >= duration) {
                    // Calculate frame rate
                    const frameRate = frameCount / duration;
                    resolve(frameRate);
                    URL.revokeObjectURL(video.src); // Clean up
                    return;
                }

                // Increment frame count
                frameCount++;

                // Seek to the next frame
                video.currentTime += 1 / 30; // Assume 30 FPS for seeking (can be adjusted)
                requestAnimationFrame(seek);
            };

            // Start seeking
            seek();
        };

        video.onerror = () => {
            reject('Error loading video metadata');
            URL.revokeObjectURL(video.src); // Clean up
        };
    });
};

export const getVideoMetadata = async (file: any, fields: Array<string>): Promise<{ codec: string; frameRate: number }> => {
    // fields can e used for costly operations.
    return new Promise((resolve, reject) => {
        if (!file) {
            reject('No file provided');
            return;
        }

        // Create a video element
        const video = document.createElement('video');
        video.src = URL.createObjectURL(file);

        let codec = 'unknown';
        let frameRate = 0;

        // Set up event listeners
        video.onloadedmetadata = async () => {
            try {
                // Step 1: Get the codec
                const chunkSize = 1024 * 1024; // 1MB
                const blob = file.slice(0, chunkSize);
                const reader = new FileReader();

                reader.onload = (e: any) => {
                    const buffer = e.target.result;
                    const uint8Array = new Uint8Array(buffer);
                    let headerText = '';

                    // Convert the first bytes to a string
                    for (let i = 0; i < uint8Array.length; i++) {
                        headerText += String.fromCharCode(uint8Array[i]);
                    }

                    // Detect codec
                    if (headerText.indexOf('avc1') !== -1) {
                        codec = 'H.264';
                    } else if (headerText.indexOf('hvc1') !== -1 || headerText.indexOf('hev1') !== -1) {
                        codec = 'H.265';
                    } else {
                        codec = 'unknown';
                    }

                    // Step 2: Calculate frame rate
                    const duration = video.duration; // Total duration in seconds
                    let frameCount = 0;

                    const seek = () => {
                        if (video.currentTime >= duration) {
                            // Calculate frame rate
                            frameRate = frameCount / duration;

                            // Resolve with both codec and frame rate
                            resolve({ codec, frameRate });
                            URL.revokeObjectURL(video.src); // Clean up
                            return;
                        }

                        // Increment frame count
                        frameCount++;

                        // Seek to the next frame
                        video.currentTime += 1 / 30; // Assume 30 FPS for seeking (can be adjusted)
                        requestAnimationFrame(seek);
                    };

                    // Start seeking
                    seek();
                };

                reader.onerror = () => {
                    reject('Error reading file for codec detection');
                };

                reader.readAsArrayBuffer(blob);
            } catch (error) {
                reject(`Error processing video metadata: ${error}`);
            }
        };

        video.onerror = () => {
            reject('Error loading video metadata');
            URL.revokeObjectURL(video.src); // Clean up
        };
    });
};

export const getAudioMetadata = async (file: any): Promise<{ channels: number; sampleRate: number }> => {
    return new Promise((resolve, reject) => {
        if (!file) {
            reject('No file provided');
            return;
        }

        // Create an audio context
        const audioContext = new AudioContext();

        // Read the file as an array buffer
        const reader = new FileReader();
        reader.onload = async (e) => {
            const arrayBuffer = e.target?.result as ArrayBuffer;

            // Decode the audio data
            audioContext.decodeAudioData(
                arrayBuffer,
                (audioBuffer) => {
                    // Extract metadata
                    const channels = audioBuffer.numberOfChannels;
                    const sampleRate = audioBuffer.sampleRate;

                    // Resolve with metadata
                    resolve({ channels, sampleRate });
                },
                (error) => {
                    reject(`Error decoding audio data: ${error}`);
                }
            );
        };

        reader.onerror = () => {
            reject('Error reading file');
        };

        reader.readAsArrayBuffer(file);
    });
};

export const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    // const timezoneOffset = -date.getTimezoneOffset() / 60; // Convert offset to hours
    // const timezoneSign = timezoneOffset >= 0 ? '+' : '-';
    // const timezone = `${timezoneSign}${String(Math.abs(timezoneOffset)).padStart(2, '0')}:00`;

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// export const getDomainData = async () => {
//     const response = await axiosClient().get(
//         `${process.env.REACT_APP_PAGE_CONFIG_SERVER}/v1/appConfig/domain_data`
//     );
//     sessionStorage.setItem('domain-data',response.data)
//     return decompressData(response.data);
// };

export const resendVerificationEmail = async (
    uuid: any,
    instituteId: any,
    link: any,
    name: any,
    instituteName: any,
    registered_email_id: any
) => {
    try {
        const response = await axiosClient().post(`${process.env.REACT_APP_BATCH_JOBS_API}/resend-verification-email`, {
            uuid,
            instituteId,
            link,
            name,
            instituteName,
            registered_email_id,
        });
        if (response.status === 200) {
            return true;
        }
    } catch (error) {
        console.error('Error sending verification email:', error);
    }
    return false;
};

// Compress and encode data to store in localStorage
const compressData = (json: any) => {
    const jsonString = JSON.stringify(json);
    const compressed = pako.deflate(jsonString);

    let binary = '';
    for (let i = 0; i < compressed.length; i++) {
        binary += String.fromCharCode(compressed[i]);
    }

    return btoa(binary);
};

// Decompress previously stored base64-encoded data
export const decompressData = (compressedData: string) => {
    try {
        const binaryString = atob(compressedData);
        const binaryData = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            binaryData[i] = binaryString.charCodeAt(i);
        }

        const decompressedBuffer = pako.inflate(binaryData);
        const decompressedData = new TextDecoder('utf-8').decode(decompressedBuffer);

        return JSON.parse(decompressedData);
    } catch (error) {
        console.error('❌ Error decompressing data:', error);
        return null;
    }
};

// Final reusable function to get domain data
export const getDomainData = async (): Promise<any> => {
    try {
        // Check cache
        const cachedData = localStorage.getItem(CACHE_KEY);
        const cachedTimestamp = localStorage.getItem(TIMESTAMP_KEY);

        if (cachedData && cachedTimestamp && Date.now() - parseInt(cachedTimestamp) < domainDataCacheExpiry) {
            console.log(' Using cached compressed domain data');
            console.log(decompressData(cachedData));
            return decompressData(cachedData);
        }

        // Fetch presigned URL
        const res = await axios.get(`${process.env.REACT_APP_API_GATEWAY_URL_FOR_DOMAIN_DATA}`);
        const presignedUrl = res?.data?.url;

        // Fetch uncompressed JSON from S3
        const response = await axios.get(presignedUrl, { responseType: 'json' });

        // Compress and store in localStorage
        const compressed = compressData(response.data);
        localStorage.setItem(CACHE_KEY, compressed);
        localStorage.setItem(TIMESTAMP_KEY, Date.now().toString());

        console.log('✅ Fetched and cached compressed domain data');
        return response.data;
    } catch (error) {
        console.error('❌ Failed to fetch domain data:', error);
        return null;
    }
};

/** User is written to both storages at login; sessionStorage is per-tab, localStorage persists across tabs. */
export const getStoredUser = (): Record<string, any> => {
    try {
        const raw = sessionStorage.getItem('user') || localStorage.getItem('user') || '{}';
        return JSON.parse(raw);
    } catch {
        return {};
    }
};

export const getStoredUserInstituteIds = (): string[] => {
    const institutes = getStoredUser()?.institutes;
    if (!Array.isArray(institutes)) return [];
    return institutes.map((v: any) => String(v)).filter((v: string) => v.length > 0);
};

export const getInstituteFlags = (
  state: State,
  institutes: string[]
): { engagementHeader: boolean; recruiter: boolean; domainExpert: boolean; mentorshipHeader: boolean } => {
  let engagementHeader = false;
  let recruiter = false;
  let domainExpert = false;
  let mentorshipHeader = false;
  const instituteIds = Array.isArray(institutes) ? institutes : [];

  try {
    for (const instituteId of instituteIds) {
      const flexCol = getFlexColDomainValueForCode(
        String(instituteId),
        "INSTITUTE_DOMAIN",
        state
      );

      if (flexCol) {
        const parsed = JSON.parse(flexCol);

        if (parsed?.engagement_header == true) {
          engagementHeader = true;
        }

        if (parsed?.recruiter_enabled == true) {
          recruiter = true;
        }

        const commitmentPaths = parsed?.commitment_paths ?? [];
        if (Array.isArray(commitmentPaths) && (commitmentPaths.includes('CG') || commitmentPaths.includes('AT'))) {
          domainExpert = true;
        }

        if (parsed?.mentorship_header == true) {
          mentorshipHeader = true;
        }
      }

      if (engagementHeader && recruiter && domainExpert && mentorshipHeader) break;
    }
  } catch (err) {
    console.error("Error in getInstituteFlags:", err);
  }

  return { engagementHeader, recruiter, domainExpert, mentorshipHeader };
};
/**
 * @func underScoreToTitleCase converting under_score string to Title Case string;
 * @param {string} str input string in under_score format;
 * @returns {string} converted string in Title Case format;
 * @dt 16th.Dec,2k25 @by RG;
 */
export const underScoreToTitleCase = (str: string): string => {
    return str
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/**
 * @func camelToTitleCase converting camelCase string to Title Case string;
 * @param {string} str input string in camelCase format;
 * @returns {string} converted string in Title Case format;
 * @dt 16th.Jan,2k26 @by RG;
 */
export const camelToTitleCase = (str: string): string => {
    const result = str.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
}

export const getLoginMode = () : string => {
    const token = localStorage.getItem('user-jwt-token') ?? "";
    if (token) {
        const decodedToken: any = jwt_decode(token);
        return decodedToken.loginMode;
    }

    return "FULL_ACCESS";
}

export const allowPerformAction = (loginMode?: string) : boolean => {
    loginMode = loginMode ?? getLoginMode();
    return loginMode !== 'LIMITED_ACCESS';
}

export const getActionElementClass = (loginMode?: string) : string => {
    return !allowPerformAction(loginMode) ? 'impersonate-action-disabled' : '';
}

export function isCityCodeDomain(domainCategory?: string | null): boolean {
    return domainCategory === 'CITY_CODE';
}

/** Shared shape for alumni / domain-expert / recruiter directory filterConfig entries. */
export type DirectoryFilterConfigItem = {
    id: string;
    label: string;
    domain?: string | null;
    filterType?: 'domain' | 'boolean';
    /** Renders in Show Profiles / Show Experts / Show Recruiters section (not main filter grid). */
    isShowProfileFilter?: boolean;
    displayOrder?: number;
    /** How domain checkbox lists are ordered when not using top-partition grouping. */
    domainSort?: 'displayOrder' | 'alpha' | 'pipeline';
    /** Top group + A–Z split (countries, cities, top companies/colleges). */
    domainGroup?: 'topPartition' | 'none';
    /** Section heading when domainGroup is topPartition (e.g. Top 10 Companies). */
    topPartitionGroupLabel?: string;
    /** Value stored when a boolean filter checkbox is checked (1 for alumni/recruiter, true for domain expert). */
    booleanCheckedValue?: 1 | true;
    applicableFor?: string;
    isAdditional?: boolean;
    isVisible?: boolean;
    [key: string]: unknown;
};

export function getShowProfileFilters(
    filterConfig: Record<string, DirectoryFilterConfigItem>
): DirectoryFilterConfigItem[] {
    return Object.values(filterConfig)
        .filter((c) => c.isShowProfileFilter)
        .sort(
            (a, b) =>
                (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
                a.label.localeCompare(b.label)
        );
}

export function getShowProfileFilterLabelMap(
    filterConfig: Record<string, DirectoryFilterConfigItem>
): Record<string, string> {
    return Object.fromEntries(getShowProfileFilters(filterConfig).map((c) => [c.id, c.label]));
}

export function getBooleanFilterDefaults(
    filterConfig: Record<string, DirectoryFilterConfigItem>
): Record<string, null> {
    return Object.fromEntries(getShowProfileFilters(filterConfig).map((c) => [c.id, null]));
}

export function isShowProfileFilterActive(value: unknown): boolean {
    return value === true || value === 1 || value === '1';
}

export function getActiveShowProfileFiltersFromConfig(
    filters: Record<string, unknown> | undefined,
    filterConfig: Record<string, DirectoryFilterConfigItem>
): { key: string; label: string }[] {
    if (!filters) return [];
    return getShowProfileFilters(filterConfig)
        .filter((c) => isShowProfileFilterActive(filters[c.id]))
        .map((c) => ({ key: c.id, label: c.label }));
}

export function usesTopPartitionDomainGroup(
    config?: Pick<DirectoryFilterConfigItem, 'domain' | 'domainGroup'>
): boolean {
    if (config?.domainGroup === 'topPartition') return true;
    if (config?.domainGroup === 'none') return false;
    return isCountryCodeDomain(config?.domain) || isCityCodeDomain(config?.domain);
}

export function sortDirectoryDomainItems(
    items: DomainElement[],
    config?: Pick<DirectoryFilterConfigItem, 'domain' | 'domainSort'>
): DomainElement[] {
    const sortMode =
        config?.domainSort ?? (config?.domain === 'WATCHLIST_STAGE' ? 'pipeline' : 'displayOrder');
    if (sortMode === 'pipeline') return items;
    if (sortMode === 'alpha') {
        return [...items].sort((a, b) => a.value.localeCompare(b.value));
    }
    return [...items].sort(
        (a, b) =>
            (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a.value.localeCompare(b.value)
    );
}

export function isDirectoryMainFilter(config: DirectoryFilterConfigItem): boolean {
    return !config.isShowProfileFilter && config.filterType !== 'boolean';
}

/** Pick width-25 (4/row) or w-20 (5/row) so the last filter row is not a single orphan item. */
export function resolveDirectoryFilterItemClass(baseClass: string, visibleCount: number): string {
    const widthClass =
        visibleCount > 0 && visibleCount % 4 !== 0 && visibleCount % 5 === 0 ? 'w-20' : 'width-25';
    return baseClass.replace(/\b(width-25|w-20)\b/g, widthClass);
}

/** Deduplicate user-entered free-text filter options (e.g. Expertise) case-insensitively. */
export function dedupeFreeTextFilterOptions(
    tags: Array<string | null | undefined>
): { code: string; value: string }[] {
    const byLower = new Map<string, string>();
    for (const tag of tags) {
        if (!tag) continue;
        const trimmed = String(tag).trim();
        if (!trimmed) continue;
        const key = trimmed.toLowerCase();
        if (!byLower.has(key)) {
            byLower.set(key, trimmed);
        }
    }
    return [...byLower.values()]
        .map((value) => ({ code: value, value }))
        .sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase()));
}

type MessagingTarget = {
    userStatusCode?: string;
    isDeceased?: number | string | boolean;
};

type ProfileMessagingContext = {
    isDeceased?: number | string | boolean;
    selectedInstitute?: { userStatusCode?: string; instituteId?: number | string };
    userInstitutes?: Array<{ userStatusCode?: string; instituteId?: number | string }>;
};

export const getProfileInstituteForMessaging = (
    profileData: ProfileMessagingContext | null | undefined,
    primaryInstituteId?: number | string
) =>
    profileData?.selectedInstitute ||
    (profileData?.userInstitutes || []).find((inst) => inst.instituteId == primaryInstituteId) ||
    (profileData?.userInstitutes || [])[0];

/** Profile header Send is allowed only for active, non-memorialized alumni. */
export const isProfileMessagingDisabled = (
    profileData: ProfileMessagingContext | null | undefined,
    primaryInstituteId?: number | string
): boolean => {
    const profileInstitute = getProfileInstituteForMessaging(profileData, primaryInstituteId);
    return Number(profileData?.isDeceased) === 1 || profileInstitute?.userStatusCode !== 'ACTIVE';
};

/** Directory card Send uses the viewed profile's userStatusCode directly. */
export const canSendDirectoryMessage = (student: MessagingTarget | null | undefined): boolean =>
    Number(student?.isDeceased) !== 1 && student?.userStatusCode === 'ACTIVE';

export const getProfileMessagingDisabledTooltip = (
    profileData: { isDeceased?: number | string | boolean } | null | undefined
): string =>
    Number(profileData?.isDeceased) === 1
        ? 'Messaging is not available for memorialized profiles'
        : 'Messaging is available for active profiles only';

export const getDirectorySendDisabledTooltip = (
    isEmailVerified: number | boolean,
    student: MessagingTarget | null | undefined
): string => {
    if (Number(isEmailVerified) !== 1) {
        return 'Please verify your email to send message';
    }
    return getProfileMessagingDisabledTooltip(student);
};

export const displayActionNotAllowedAlert = () : void => {
    Swal.fire({
        icon: 'warning',
        title: 'Access Denied..!',
        text: 'You can not perform this action in impersonation mode..!',
        // showConfirmButton: false,
        allowOutsideClick: false,
    });
}

// @UAS-2071: Allowed URLs for LIMITED_ACCESS mode
export const getLimitedAccessAllowedUrls = (): (string | RegExp)[] => {
    return [
        '/management/dashboard',
        // '/alumni/dashboard',
        '/alumni/directory',
        '/alumni-review/internal',
        // Pattern for alumni/{USER_UUID}/profile/{pageSlug}
        /^\/management\/[a-f0-9\-]{36}\/profile\/?$/i,
        /^\/alumni\/[a-f0-9\-]{36}\/profile\/?$/i,
        /^\/alumni\/[a-f0-9\-]{36}\/select-institute\/?$/i,
        /^\/alumni\/[a-f0-9\-]{36}\/profile\/[\w\-]+$/i,

        // Menus under MANAGE
        '/recruiter/directory',
        '/lead/managelead',
        /^\/lead\/lead-profile\?id=[0-9]+$/i,
        '/program/listing',
        '/qna-dashboard',
        /^\/view-more-question\?question_id=[0-9]+$/i,
        '/socialmedia/social-integration',
        '/management/grievance-v2',
        '/support/business-directory',
        
        // Menus under Track parent menu;
        '/management/track/student-profiles',
        '/management/track/student-career-preferences',
        '/management/track/alumni-profiles',
        '/management/track/alumni-education-preferences',
        '/management/track/alumni-engagement-with-students',
        '/management/track/activity-log-report',

        // Menus under Engagement parent menu;
        '/broadcast',



    ];
}

// @UAS-2071: Check if current URL is allowed in LIMITED_ACCESS mode
export const isUrlAllowedInLimitedAccess = (currentPath: string): boolean => {
    const loginMode = getLoginMode();
    
    // If not in LIMITED_ACCESS mode, allow all URLs
    if (loginMode !== 'LIMITED_ACCESS') {
        return true;
    }

    const allowedUrls = getLimitedAccessAllowedUrls();
    
    // Check if current path matches any allowed URL
    return allowedUrls.some(allowedUrl => {
        if (allowedUrl instanceof RegExp) {
            // Use regex matching for RegExp patterns
            return allowedUrl.test(currentPath);
        } else {
            // Use string matching for string patterns
            return currentPath === allowedUrl || currentPath.startsWith(allowedUrl + '/');
        }
    });
}

/**
 * commenting due to tooltips are not initializing in many places due to 
 * DOM issue, Like ajax, Corousel etc. Need to handle all such places first.
 */
// export const initializeImpersonateTooltips = () => {
//     const tooltipTriggerList = [].slice.call(document.querySelectorAll('.impersonate-action-disabled'));
//     const tooltipList = tooltipTriggerList.map((tooltipTriggerEl) => {
//         // Dispose existing tooltip if it exists
//         const existingTooltip = Tooltip.getInstance(tooltipTriggerEl);
//         if (existingTooltip) {
//             existingTooltip.dispose();
//         }
//         return new Tooltip(tooltipTriggerEl, {
//             title: "You can't perform this action in impersonation mode",
//             trigger: 'hover',
//             html: true,
//             customClass: 'tooltip-html',
//             container: 'body',
//             delay: { show: 0, hide: 100 },
//         });
//     });
//     return tooltipList;
// };
